import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { useAppSelector } from '@/redux/rootReducer'
import {
  selectCpxCardPracticeLocationIndex,
  selectDMPConnectTlsiConnector,
  selectPracticeLocations,
} from '@/redux/reducers/dmp-connect.reducer'

import { getBillingNumberFromPracticeLocation } from '../lib/getBillingNumberFromPracticeLocation'
import { useCreateInsiConnector } from './useCreateInsiConnector'
import { useTlsiConnectorAutoCreate } from './useTlsiConnectorAutoCreate'
import { useTlsiConnectorActions } from './useTlsiConnectorActions'

const formSchema = yup.object({
  billingNumber: yup.string().required(),
  practiceLocation: yup.number().required(),
  practiceSetting: yup.string().required(),
})

export type TlsiConnectorFormData = yup.InferType<typeof formSchema>

export function useTlsiConnectorForm() {
  const cpxCardPracticeLocationIndex = useAppSelector(selectCpxCardPracticeLocationIndex)
  const { billingNumber, practiceSetting } = useAppSelector(selectDMPConnectTlsiConnector) ?? {}
  const practiceLocations = useAppSelector(selectPracticeLocations)

  const selectedPracticeLocation = cpxCardPracticeLocationIndex != null ? practiceLocations?.[cpxCardPracticeLocationIndex] : undefined
  const selectedPracticeLocationBillingNumber = selectedPracticeLocation ? getBillingNumberFromPracticeLocation(selectedPracticeLocation) : undefined

  const rhForm = useForm<TlsiConnectorFormData>({
    defaultValues: {
      billingNumber: billingNumber || selectedPracticeLocationBillingNumber || '',
      practiceLocation: cpxCardPracticeLocationIndex ?? NaN,
      practiceSetting: practiceSetting ?? '',
    },
    resolver: yupResolver(formSchema),
  })

  const {
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
  } = rhForm

  const { createInsiConnector, isCreatingInsiConnector } = useCreateInsiConnector()
  const { resetCard, selectPracticeLocation } = useTlsiConnectorActions(rhForm.setValue)

  useTlsiConnectorAutoCreate({
    handleSubmit,
    createInsiConnector,
    reset,
  })

  const onSubmit: SubmitHandler<TlsiConnectorFormData> = async (data) => {
    await createInsiConnector(data.billingNumber, data.practiceLocation, data.practiceSetting)
    reset(data)
  }

  return {
    rhForm,
    isDirty,
    isValid,
    isCreatingInsiConnector,
    onSubmit,
    resetCard,
    selectPracticeLocation,
    practiceLocations,
  }
}
